import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown } from 'antd';
import { getUserProfile, logout } from '@/api/system';
import type { UserProfile } from '@/api/system';
import { getFileAccessUrl } from '@/utils/fileUrl';
import { clearAuthSession } from '@/utils/auth';

interface HeaderProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  pageTitle: string;
}

export default function Header({ collapsed, onCollapse, pageTitle }: HeaderProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    let ignore = false;
    getUserProfile()
      .then((data) => {
        if (!ignore) setProfile(data);
      })
      .catch(() => {
        if (!ignore) setProfile(null);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const avatarUrl = useMemo(() => {
    const avatar = profile?.avatar;
    if (!avatar) return '';
    return getFileAccessUrl(avatar, 'path');
  }, [profile?.avatar]);

  const displayName = profile?.nickname || profile?.username || '管理员';
  const roleText = profile?.roleNames || profile?.deptName || '管理员';

  const handleMenuClick = useCallback(async ({ key }: { key: string }) => {
    if (key === 'logout') {
      try {
        await logout();
      } catch {
        // 本地退出优先，后端 token 已失效或网络异常时也清理前端会话。
      }
      clearAuthSession();
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/login');
      }
    }
    if (key === 'profile') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/settings/accounts');
      }
    }
    if (key === 'settings') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/settings/accounts');
      }
    }
    if (key === 'change-password') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/profile/change-password');
      }
    }
  }, []);

  const userMenuItems = [
    { key: 'profile', label: '个人中心' },
    { key: 'settings', label: '账号设置' },
    { key: 'change-password', label: '修改密码' },
    { type: 'divider' as const },
    { key: 'logout', label: '退出登录', danger: true },
  ];

  return (
    <header
      className="fixed top-0 right-0 h-14 bg-white border-b border-gray-100 flex items-center justify-between px-6 z-40 transition-all duration-300"
      style={{ left: collapsed ? 64 : 240 }}
    >
      {/* Left: Sidebar Toggle and Page Title */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onCollapse(!collapsed)}
          aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
          aria-expanded={!collapsed}
          title={collapsed ? '展开侧边栏' : '收起侧边栏'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-lg text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 transition-colors"
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>
        <h1 className="text-lg font-medium text-gray-800">{pageTitle}</h1>
      </div>

      {/* Right: User */}
      <div className="flex items-center gap-5">
        {/* User */}
        <Dropdown menu={{ items: userMenuItems, onClick: handleMenuClick }} placement="bottomRight">
          <div className="flex items-center gap-2 cursor-pointer">
           {/*  <Avatar
              size="small"
              src={avatarUrl || undefined}
            >
              {!avatarUrl ? displayName.slice(0, 1).toUpperCase() : null}
            </Avatar> */}
            <div className="hidden md:flex flex-col">
              <span className="text-sm text-gray-800 font-medium">{displayName}</span>
              <span className="text-xs text-gray-500">{roleText}</span>
            </div>
            <DownOutlined className="text-xs text-gray-400" />
          </div>
        </Dropdown>
      </div>
    </header>
  );
}
